/**
 * TeamMintPanel.jsx
 *
 * Owner-only reserve minting. Rendered only when the connected wallet
 * matches the contract's `owner()`, so it is invisible to everyone else.
 *
 * That check is a UI convenience, not a security boundary — `teamMint`
 * is `onlyOwner` on-chain, so a non-owner calling it directly reverts
 * regardless of what this component does or doesn't render.
 *
 * Why the batch guidance: measured cost is ~115,786 gas per token, so
 * minting the full 222 reserve in one call needs ~25.7M gas — over the
 * block limit on most chains, and it simply fails. 25 per transaction
 * (~2.9M gas) is comfortable.
 */
import React, { useState } from 'react';
import Panel from '../Panel';
import GlowText from '../GlowText';
import RoboticButton from '../RoboticButton';

const SAFE_BATCH = 25;

export default function TeamMintPanel({ teamMinted, totalSupply, maxSupply, onMint, pending }) {
  const [qty, setQty] = useState(SAFE_BATCH);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);

  const remaining = maxSupply - totalSupply;
  const tooBig = qty > SAFE_BATCH;

  const go = async () => {
    setError(null); setOk(null);
    try {
      await onMint(qty);
      setOk(`Minted ${qty}.`);
    } catch (e) {
      setError(e?.message || 'Team mint failed.');
    }
  };

  return (
    <Panel title="TEAM RESERVE // OWNER ONLY" bodyClassName="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="hud-chip">
          <span className="font-mono-hud text-dim text-xs">TEAM MINTED</span>
          <GlowText text={String(teamMinted)} size={16} variant="cyan" mono />
        </div>
        <div className="hud-chip">
          <span className="font-mono-hud text-dim text-xs">SUPPLY LEFT</span>
          <GlowText text={String(remaining)} size={16} variant="violet" mono />
        </div>
      </div>

      <p className="font-mono-hud text-dim text-fluid-sm">
        Free apart from gas, and works in any phase — including before the
        sale opens. Mint the reserve before GTD starts, or you compete with
        your own minters for the same supply.
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setQty((q) => Math.max(1, q - 1))}
          className="btn-robotic btn-robotic-ghost w-10 h-10 !px-0 !py-0 text-lg"
          disabled={qty <= 1 || pending}
        >
          −
        </button>
        <input
          type="number" min="1" max={remaining}
          value={qty}
          onChange={(e) => setQty(Math.max(1, Math.min(remaining, Number(e.target.value) || 1)))}
          className="hud-input"
          style={{ width: 110, textAlign: 'center' }}
          disabled={pending}
        />
        <button
          type="button"
          onClick={() => setQty((q) => Math.min(remaining, q + 1))}
          className="btn-robotic btn-robotic-ghost w-10 h-10 !px-0 !py-0 text-lg"
          disabled={qty >= remaining || pending}
        >
          +
        </button>
        <button
          type="button"
          onClick={() => setQty(SAFE_BATCH)}
          className="btn-robotic btn-robotic-ghost px-4 py-2 text-xs"
          disabled={pending}
        >
          SET {SAFE_BATCH}
        </button>
      </div>

      {tooBig && (
        <div className="mech-panel" style={{ padding: '10px 16px', borderColor: 'var(--danger)' }}>
          <span className="font-mono-hud text-fluid-sm glow-danger">
            Over {SAFE_BATCH} per transaction risks exceeding the block gas
            limit. Mint in batches of {SAFE_BATCH} instead — for the full 222
            reserve that is {SAFE_BATCH} × 8 then 22.
          </span>
        </div>
      )}

      <RoboticButton
        variant={tooBig ? 'danger' : 'violet'}
        size="lg" fullWidth
        disabled={pending || remaining <= 0}
        onClick={go}
      >
        {pending ? 'CONFIRMING…' : `TEAM MINT ${qty}`}
      </RoboticButton>

      {error && <p className="font-mono-hud text-fluid-sm text-center glow-danger">{error}</p>}
      {ok && <p className="font-mono-hud text-fluid-sm text-center glow-cyan">{ok}</p>}
    </Panel>
  );
}
